import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOPageMeta from '@/components/feature/SEOPageMeta';
import { tvShows, categories } from '@/mocks/tvshows';

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'new'>('rating');

  const currentCategory = categories.find((c) => c.slug === category);
  const categoryName = currentCategory?.name || category || '';

  const filteredShows = useMemo(() => {
    if (!category) return tvShows;
    return tvShows.filter((s) => s.category === category);
  }, [category]);

  const sortedShows = useMemo(() => {
    const shows = [...filteredShows];
    if (sortBy === 'rating') return shows.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'year') return shows.sort((a, b) => b.year - a.year);
    if (sortBy === 'new') {
      return shows.sort((a, b) => {
        if (a.new && !b.new) return -1;
        if (!a.new && b.new) return 1;
        return b.year - a.year;
      });
    }
    return shows;
  }, [filteredShows, sortBy]);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <SEOPageMeta
        title={`${categoryName} - 分类浏览`}
        description={`久播影院${categoryName}频道，提供最新${categoryName}全集免费在线观看，高清不缓冲，追剧首选平台。`}
        keywords={`久播影院,${categoryName},电视剧大全,免费在线观看`}
        canonicalUrl={`https://www.jiubocinema.com/category/${category || 'guochan'}`}
      />
      <div className="pt-16">
        <div className="max-w-screen-2xl mx-auto">
          <Breadcrumb
            items={[
              { label: '分类', href: '/category/guochan' },
              { label: categoryName },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-6 pb-2">
          <div className="max-w-screen-2xl mx-auto">
            <div className="bg-background-100 rounded-lg p-3 flex items-center gap-2 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-full whitespace-nowrap transition-all ${
                    category === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-bold text-foreground-900">
                {categoryName || '全部剧集'}
                <span className="text-sm text-foreground-500 font-normal ml-2">
                  共 {filteredShows.length} 部
                </span>
              </h2>

              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {[
                  { key: 'rating' as const, label: '评分最高' },
                  { key: 'year' as const, label: '最新上线' },
                  { key: 'new' as const, label: '最新更新' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 text-xs rounded-md transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-900 font-medium shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedShows.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-film-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的剧集</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {sortedShows.map((show) => (
                  <Link
                    key={show.id}
                    to={`/article/${show.id}`}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2.5">
                      <img
                        src={show.posterImage}
                        alt={show.title}
                        title={`${show.title} - 久播影院`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 text-[10px] font-semibold bg-primary-500 text-background-50 rounded whitespace-nowrap">
                          {show.rating}
                        </span>
                      </div>
                      {show.status === '更新中' && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 text-[10px] bg-accent-500 text-background-50 rounded whitespace-nowrap">
                            更新中
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2.5">
                        <p className="text-xs text-background-50/90">
                          {show.episodes}集 · {show.status}
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {show.title}
                    </h3>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-foreground-500">{show.genres[0]}</span>
                      <span className="text-xs text-foreground-400">{show.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}